/* <bb-map>: the one shared Beds & Bedding map (store locator + store page). Presentation only — coordinates, names and
   slugs come from the platform's public store DTOs (never computed or stored here). Google Maps JS is loaded once, lazily
   (first time a map is near the viewport or explicitly shown), only on templates that render this element, with the
   referrer-restricted browser key from the theme setting `google_maps_browser_key` (never in Git). Without a key, on a
   load error or when Maps is blocked the element stays hidden and dispatches `bb-map:error` — the list is the product,
   the map is enhancement. Markers are inline SVG data URIs (navy pin, gold star; selected = gold pin), crisp on any DPI.
   Events: `bb-map:select { slug }` (marker click). API: setStores(stores), select(slug), fit(slugs), show(). */
(function () {
  'use strict';
  const BB = window.BB;
  let loader = null;

  /* Calm, light styling: muted roads/labels, no POI clutter — brand markers stay the dominant points. Google-required
     attribution/controls are untouched. */
  const STYLES = [
    { elementType: 'geometry', stylers: [{ color: '#f4f3f5' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#586776' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff' }] },
    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', stylers: [{ visibility: 'off' }] },
    { featureType: 'administrative', elementType: 'geometry', stylers: [{ visibility: 'off' }] },
    { featureType: 'landscape.natural', elementType: 'geometry', stylers: [{ color: '#eceef0' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
    { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#e3e0e5' }] },
    { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#f2e2b7' }] },
    { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#e8d6a6' }] },
    { featureType: 'road', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#d6dde3' }] },
    { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#88939d' }] }
  ];

  /* Brand marker (34×44 CSS px, SVG so it stays sharp on retina). Default: navy pin + gold star; selected: gold pin + navy star. */
  function markerSvg(selected) {
    const pin = selected ? '#ecd598' : '#10263b';
    const star = selected ? '#10263b' : '#ecd598';
    const stroke = selected ? '#10263b' : '#ffffff';
    return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(
      '<svg xmlns="http://www.w3.org/2000/svg" width="34" height="44" viewBox="0 0 34 44">' +
      '<path d="M17 1.5C8.4 1.5 1.5 8.3 1.5 16.7c0 10.6 12.6 23.2 14.5 25.1a1.4 1.4 0 0 0 2 0c1.9-1.9 14.5-14.5 14.5-25.1C32.5 8.3 25.6 1.5 17 1.5z" fill="' + pin + '" stroke="' + stroke + '" stroke-width="1.5"/>' +
      '<path d="M17 8.5c.9 4.6 3.5 7.2 8.1 8.1-4.6.9-7.2 3.5-8.1 8.1-.9-4.6-3.5-7.2-8.1-8.1 4.6-.9 7.2-3.5 8.1-8.1z" fill="' + star + '"/>' +
      '</svg>');
  }

  /* Loads Google Maps JS once for the whole page; resolves with `google.maps`. Never injects a second script. */
  function loadMaps() {
    if (loader) return loader;
    const key = BB.config.maps && BB.config.maps.key;
    loader = new Promise((resolve, reject) => {
      if (window.google && window.google.maps && window.google.maps.Map) return resolve(window.google.maps);
      if (!key) return reject(new Error('maps-not-configured'));
      if (document.querySelector('script[data-bb-maps]')) {
        const wait = setInterval(() => { if (window.google && window.google.maps && window.google.maps.Map) { clearInterval(wait); resolve(window.google.maps); } }, 100);
        setTimeout(() => { clearInterval(wait); reject(new Error('maps-timeout')); }, 15000);
        return;
      }
      const cb = '__bbMapsReady';
      window[cb] = () => { delete window[cb]; resolve(window.google.maps); };
      const s = document.createElement('script');
      s.src = 'https://maps.googleapis.com/maps/api/js?key=' + encodeURIComponent(key) + '&v=weekly&loading=async&language=nl&region=NL&callback=' + cb;
      s.async = true; s.defer = true; s.setAttribute('data-bb-maps', '');
      s.onerror = () => reject(new Error('maps-load-failed'));
      /* Maps reports an invalid/blocked key through this global instead of onerror. */
      window.gm_authFailure = () => { loader = null; reject(new Error('maps-auth-failed')); document.querySelectorAll('bb-map').forEach((m) => m.fail()); };
      document.head.appendChild(s);
    });
    loader.catch(() => { loader = null; });
    return loader;
  }

  class BBMap extends HTMLElement {
    connectedCallback() {
      this.stores = [];
      this.markers = new Map();
      this.selected = null;
      this.state = 'idle';
      this.canvas = this.querySelector('[data-map-canvas]');
      this.fallback = this.querySelector('[data-map-fallback]');
      if (!this.canvas) return;
      const seed = this.getAttribute('data-stores');
      if (seed) { try { this.stores = JSON.parse(seed).filter(valid); } catch (e) { /* ignore */ } }
      /* Lazy: init when near the viewport (or when show() is called from a list/map toggle). */
      if ('IntersectionObserver' in window && !this.hasAttribute('data-eager')) {
        this.io = new IntersectionObserver((entries) => { if (entries.some((en) => en.isIntersecting)) { this.io.disconnect(); this.show(); } }, { rootMargin: '200px' });
        this.io.observe(this);
      } else this.show();
    }
    disconnectedCallback() { if (this.io) this.io.disconnect(); }

    /* Initialises the map once (idempotent); safe to call again on resize/toggle. */
    show() {
      if (this.state === 'ready') { this.fitAll(); return Promise.resolve(); }
      if (this.state === 'loading') return this.pending;
      if (!(BB.config.maps && BB.config.maps.key)) { this.fail(); return Promise.resolve(); }
      this.state = 'loading';
      this.pending = loadMaps().then((maps) => {
        this.map = new maps.Map(this.canvas, {
          center: { lat: 52.37, lng: 4.9 }, zoom: 9, styles: STYLES,
          disableDefaultUI: true, zoomControl: true, gestureHandling: 'cooperative', clickableIcons: false, backgroundColor: '#f4f3f5',
          zoomControlOptions: { position: maps.ControlPosition.RIGHT_BOTTOM }
        });
        this.state = 'ready';
        this.setAttribute('data-map-state', 'ready');
        if (this.fallback) this.fallback.hidden = true;
        this.renderMarkers();
        this.fitAll();
        this.dispatchEvent(new CustomEvent('bb-map:ready', { bubbles: true }));
      }).catch(() => this.fail());
      return this.pending;
    }
    fail() {
      this.state = 'error';
      this.setAttribute('data-map-state', 'error');
      if (this.fallback) this.fallback.hidden = false;
      this.dispatchEvent(new CustomEvent('bb-map:error', { bubbles: true }));
    }

    /* Stores: [{ slug, name, lat, lng }] from the platform DTOs. Replaces the marker set. */
    setStores(stores) {
      this.stores = (stores || []).filter(valid);
      if (this.state === 'ready') { this.renderMarkers(); this.fitAll(); }
    }
    renderMarkers() {
      const maps = window.google.maps;
      this.markers.forEach((m) => m.setMap(null));
      this.markers.clear();
      this.stores.forEach((s) => {
        const marker = new maps.Marker({
          position: { lat: s.lat, lng: s.lng }, map: this.map, title: s.name, optimized: false,
          icon: this.icon(s.slug === this.selected)
        });
        marker.addListener('click', () => { this.select(s.slug); this.dispatchEvent(new CustomEvent('bb-map:select', { bubbles: true, detail: { slug: s.slug } })); });
        this.markers.set(s.slug, marker);
      });
    }
    icon(selected) {
      const maps = window.google.maps;
      return { url: markerSvg(selected), scaledSize: new maps.Size(selected ? 40 : 34, selected ? 52 : 44), anchor: new maps.Point(selected ? 20 : 17, selected ? 52 : 44) };
    }
    /* Highlights one marker (both directions: card → marker and marker → card use this). */
    select(slug, pan) {
      this.selected = slug || null;
      if (this.state !== 'ready') return;
      this.markers.forEach((m, key) => { const on = key === this.selected; m.setIcon(this.icon(on)); m.setZIndex(on ? 10 : 1); });
      const store = this.stores.find((s) => s.slug === this.selected);
      if (store && pan !== false) this.map.panTo({ lat: store.lat, lng: store.lng });
    }
    /* Fits the given slugs (default: all stores) with a comfortable zoom cap. */
    fit(slugs) {
      if (this.state !== 'ready') return;
      const maps = window.google.maps;
      const list = this.stores.filter((s) => !slugs || slugs.indexOf(s.slug) !== -1);
      if (!list.length) return;
      if (list.length === 1) { this.map.setCenter({ lat: list[0].lat, lng: list[0].lng }); this.map.setZoom(Number(this.getAttribute('data-zoom')) || 14); return; }
      const bounds = new maps.LatLngBounds();
      list.forEach((s) => bounds.extend({ lat: s.lat, lng: s.lng }));
      this.map.fitBounds(bounds, 56);
      maps.event.addListenerOnce(this.map, 'idle', () => { if (this.map.getZoom() > 13) this.map.setZoom(13); });
    }
    fitAll() { this.fit(null); }
  }
  function valid(s) { return s && typeof s.slug === 'string' && typeof s.lat === 'number' && typeof s.lng === 'number' && isFinite(s.lat) && isFinite(s.lng); }

  if (!customElements.get('bb-map')) customElements.define('bb-map', BBMap);
  BB.map = { markerSvg: markerSvg, STYLES: STYLES, loadMaps: loadMaps, valid: valid };
})();
